import { Div, state } from '@codesuma/baseline'
import settings, { YearFormat, MonthNames, Theme } from '../../../services/settings'
import theme from '../../../services/theme'
import reminders from '../../../services/reminders'
import phoneCalendar from '../../../services/phone-calendar'
import phoneReminders from '../../../services/phone-reminders'
import calendar from '../../../services/calendar'
import { J_WEEKDAYS } from '../../../configs/names'
import { MIRROR_CALENDARS } from '../../../configs/calendars'
import { solarYear } from '../../../helpers/year'
import { Choice } from '../../../components/choice'
import style from './index.module.css'

// The app's settings, shown in the menu under its links: the theme, what the
// day cells show, how a solar year is written, the first day of a Gregorian week, the
// phone's calendar on native, and whether reminders are delivered at all. The
// list re-renders on every settings change, because the secondary-calendar
// label and the week-start group both depend on the active system. The menu
// calls refresh each time it opens.

const YEAR_FORMATS: { value: YearFormat, label: string }[] = [
    { value: 'both', label: 'شاهنشاهی و جلالی' },
    { value: 'shahanshahi', label: 'شاهنشاهی' },
    { value: 'jalaali', label: 'جلالی' },
]

const MONTH_NAMES: { value: MonthNames, label: string }[] = [
    { value: 'iranian', label: 'ایرانی — فروردین، اردیبهشت، خرداد' },
    { value: 'afghan', label: 'افغانی — حمل، ثور، جوزا' },
]

// In the order the home page's theme button walks them, and by the names it
// gives them.
const THEMES: { value: Theme, label: string }[] = [
    { value: 'light', label: 'منظرهٔ روز' },
    { value: 'dark', label: 'منظرهٔ شب' },
    { value: 'paper', label: 'سادهٔ روشن' },
    { value: 'slate', label: 'سادهٔ تیره' },
    { value: 'seasons', label: 'رنگی' },
]

export const Settings = () => {
    // Set when the phone refuses calendar access. Out here because saying so
    // re-renders the page — the switch has to go back — and the reason has to
    // survive that render and be drawn again with it.
    let denied = false

    // Whether the phone or the browser will show a notification at all — the
    // half of the reminder switch the app does not own. Asked again on every
    // visit, because only the device's own settings can change it, and they
    // can be changed while this page is away.
    let notifying = false

    // The phone's calendars, for the list under the events switch. Read again
    // on every visit too: an account can be added in the phone's settings
    // while this page is away. The calendar older versions filled with
    // Emrouz's own occasions is not one to read from, so it is left out.
    let calendars: { id: string, name: string }[] = []

    // The same pair for the Reminders app on an iPhone: whether access was
    // refused, and its lists.
    let reminderDenied = false
    let reminderLists: { id: string, name: string }[] = []

    const list = Div()

    const group = (text: string) => {
        const heading = Div(text)
        heading.addClass(style.group)
        list.append(heading)
    }

    // What a switch does, or why it went back. Quieter than a row: it
    // explains, it does not offer anything.
    const note = (text: string) => {
        const hint = Div(text)
        hint.addClass(style.note)
        list.append(hint)
    }

    const render = () => {
        list.empty()
        const s = settings.get()

        // The same five the button on the home page walks through, for whoever
        // never finds that button. Through the theme service, so a pick here
        // dissolves the same way a tap there does.
        group('زمینه')
        for (const t of THEMES) {
            const row = Choice('radio', t.label, { name: 'theme', checked: s.theme === t.value })
            row.on('pick', () => theme.set(t.value))
            list.append(row)
        }

        // Inside the day cells: the other solar date, and the hijri one. The
        // primary calendar is switched on the home page itself.
        group('نمایش')
        const secondary = Choice('checkbox', s.system === 'gregorian' ? 'نمایش تاریخ شمسی' : 'نمایش تاریخ میلادی',
            { checked: s.showSecondary })
        secondary.on('pick', (value: boolean) => settings.update({ showSecondary: value }))
        const hijri = Choice('checkbox', 'نمایش تاریخ قمری', { checked: s.showHijri })
        hijri.on('pick', (value: boolean) => settings.update({ showHijri: value }))
        const vertical = Choice('checkbox', 'چیدمان عمودی', { checked: s.vertical })
        vertical.on('pick', (value: boolean) => settings.update({ vertical: value }))
        list.append(secondary, hijri, vertical)

        // How a solar year is written wherever one is shown. Shahanshahi is the
        // same calendar with the imperial epoch, so this is a year format and
        // not a calendar of its own. Each choice is labelled with this year
        // written that way, so the shape of it is visible before it is picked.
        group('نمایش سال شمسی')
        for (const f of YEAR_FORMATS) {
            const row = Choice('radio', `${f.label} — ${solarYear(calendar.today().jy, f.value)}`,
                { name: 'year-format', checked: s.yearFormat === f.value })
            row.on('pick', () => settings.update({ yearFormat: f.value }))
            list.append(row)
        }

        // The same twelve months, by the names Iran uses or the ones
        // Afghanistan does. Each is labelled with its first three months.
        group('نام ماه‌های شمسی')
        for (const n of MONTH_NAMES) {
            const row = Choice('radio', n.label, { name: 'month-names', checked: s.monthNames === n.value })
            row.on('pick', () => settings.update({ monthNames: n.value }))
            list.append(row)
        }

        // Only offered for the Gregorian grid: the Persian week always begins
        // on شنبه, so the choice would have no effect on a Jalaali month.
        if (s.system === 'gregorian') {
            group('اولین روز هفته میلادی')
            // Persian weekday indices: 0 = شنبه, 1 = یکشنبه, 2 = دوشنبه
            for (const wd of [0, 1, 2]) {
                const day = Choice('radio', J_WEEKDAYS[wd], { name: 'week-start', checked: s.weekStart === wd })
                day.on('pick', () => settings.update({ weekStart: wd }))
                list.append(day)
            }
        }

        // Native only: a browser has no phone calendar to read — and a switch
        // nobody can find is a switch that does not exist.
        if (phoneCalendar.available()) {
            group('تقویم گوشی')

            // Reading only: Emrouz shows the phone's events and writes nothing
            // back. The phone prompts the first time and answers from its own
            // settings ever after, so a refusal has to put the switch back.
            const events = Choice('checkbox', 'نمایش رویدادهای تقویم گوشی', { checked: s.showPhoneEvents })
            events.on('pick', async (value: boolean) => {
                denied = false
                if (!value) {
                    settings.update({ showPhoneEvents: false })
                    return
                }
                denied = !(await phoneCalendar.enable())
                settings.update({ showPhoneEvents: !denied })
                if (!denied) readCalendars()
            })
            list.append(events)
            if (denied) note('دسترسی به تقویم گوشی داده نشد. می‌توانید آن را از تنظیمات گوشی روشن کنید.')

            // Which calendars the events come from, one row each, set in under
            // the switch they answer to. A new connection has none ticked. An
            // install from before there was a choice reads them all, so all of
            // them show ticked, and the first untick keeps the rest.
            if (s.showPhoneEvents && calendars.length) {
                note('رویدادهای کدام تقویم‌ها نمایش داده شود؟')
                const chosen = s.phoneCalendars ?? calendars.map(c => c.id)
                for (const c of calendars) {
                    const row = Choice('checkbox', c.name, { checked: chosen.includes(c.id) })
                    row.addClass(style.nested)
                    row.on('pick', (value: boolean) => settings.update({
                        phoneCalendars: value ? [...chosen, c.id] : chosen.filter(id => id !== c.id),
                    }))
                    list.append(row)
                }
            }

            // iPhone only: the tasks kept in the Reminders app, read the same
            // way and asked for the same way.
            if (phoneReminders.available()) {
                const tasks = Choice('checkbox', 'نمایش کارهای برنامه‌ی یادآورها', { checked: s.showPhoneReminders })
                tasks.on('pick', async (value: boolean) => {
                    reminderDenied = false
                    if (!value) {
                        settings.update({ showPhoneReminders: false })
                        return
                    }
                    reminderDenied = !(await phoneReminders.enable())
                    settings.update({ showPhoneReminders: !reminderDenied })
                    if (!reminderDenied) readReminderLists()
                })
                list.append(tasks)
                if (reminderDenied) note('دسترسی به یادآورها داده نشد. می‌توانید آن را از تنظیمات گوشی روشن کنید.')

                // Which lists, once there is more than one to choose between.
                // Every one shows ticked until the first is unticked.
                if (s.showPhoneReminders && reminderLists.length > 1) {
                    note('کارهای کدام فهرست‌ها نمایش داده شود؟')
                    const chosen = s.phoneReminderLists ?? reminderLists.map(l => l.id)
                    for (const l of reminderLists) {
                        const row = Choice('checkbox', l.name, { checked: chosen.includes(l.id) })
                        row.addClass(style.nested)
                        row.on('pick', (value: boolean) => settings.update({
                            phoneReminderLists: value ? [...chosen, l.id] : chosen.filter(id => id !== l.id),
                        }))
                        list.append(row)
                    }
                }
            }
        }

        // Reminders, on every platform: the browser shows them too, for as long
        // as the app is open. Two things have to be true for one to arrive, and
        // only one of them is a setting — so the switch reads the platform's
        // answer as well, and says so when that is the half saying no.
        group('یادآوری‌ها')
        const notify = Choice('checkbox', 'ارسال یادآوری', { checked: notifying && !s.remindersOff })
        notify.on('pick', async (value: boolean) => {
            if (!value) {
                settings.update({ remindersOff: true })
                return
            }
            // The device prompts the first time and answers from its own
            // settings ever after, so a refusal has to put the switch back.
            notifying = await reminders.enable()
            settings.update({ remindersOff: false })
        })
        list.append(notify)
        note(notifying
            ? 'یادآوری برای کارها و رویدادهایی فرستاده می‌شود که خودتان می‌سازید و برایشان یادآوری انتخاب می‌کنید.'
            : 'اجازه‌ی نمایش اعلان داده نشده است. می‌توانید آن را از تنظیمات دستگاه روشن کنید.')
    }

    const readPermission = () => reminders.allowed().then(ok => {
        if (ok === notifying) return
        notifying = ok
        render()
    })

    const readCalendars = () => phoneCalendar.calendars().then(found => {
        const next = found.filter(c => !MIRROR_CALENDARS.includes(c.name))
        if (JSON.stringify(next) === JSON.stringify(calendars)) return
        calendars = next
        render()
    })

    const readReminderLists = () => phoneReminders.lists().then(found => {
        if (JSON.stringify(found) === JSON.stringify(reminderLists)) return
        reminderLists = found
        render()
    })

    const refresh = () => {
        readPermission()
        readCalendars()
        readReminderLists()
    }

    render()
    refresh()
    state.on('settings', render)
    list.on('unmounted', () => state.off('settings', render))

    return Object.assign(list, { refresh })
}
